import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './editMyBump.css';
import { doc, onSnapshot, setDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../firebase';
import placeholder_color_thumbnail from '../../../assets/images/thumbnail/placeholder-color-thumbnail.jpg';
import placeholder_dark_thumbnail from '../../../assets/images/thumbnail/placeholder-dark-thumbnail.jpg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faImage } from '@fortawesome/free-solid-svg-icons';
import PreviewBumpPopup from './PreviewBumpPopup';
import useResponsiveDesign from '../../../components/appComponents/useResponsiveDesign';



const DEFAULT_DESCRIPTION = 'Timestamps:\n{{VIDEO_TIMESTAMPS}}';
const MAX_TAG_CHARS = 500;

// Title / description / thumbnail / tags for a bump. Firestore (publish_*) is the source of truth —
// it's what postYoutube sends — so the form loads from it and Save writes back to it.
const UserEditBumpSection = ({ videoId, user, isProcessedbyAI, summary, takeaways, hashtags, timestamps, isTranscriptEmpty }) => {
    const device = useResponsiveDesign();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [tags, setTags] = useState([]);
    const [saved, setSaved] = useState(null); // last saved { title, description, tags }
    const [isSaving, setIsSaving] = useState(false);
    const [justSaved, setJustSaved] = useState(false);
    const [tagInput, setTagInput] = useState('');
    const [editingIndex, setEditingIndex] = useState(-1);
    const [editingTag, setEditingTag] = useState('');
    const [thumbnailUrl, setThumbnailUrl] = useState(placeholder_color_thumbnail);
    const [localThumbnailUrl, setLocalThumbnailUrl] = useState(null);
    const [isThumbnailUploading, setIsThumbnailUploading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const fileInputRef = useRef(null);

    const bumpRef = doc(db, 'users', user.uid, 'bumpContent', `bump-${videoId}`);

    // Load the saved fields once; a new bump starts from the file name and the user's description template
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const snap = await getDoc(bumpRef);
                if (!snap.exists() || cancelled) return;
                const d = snap.data();
                let desc = d.publish_description;
                if (!desc) {
                    const profile = await getDoc(doc(db, 'users', user.uid, 'userSettings', 'profile'));
                    desc = profile.data()?.userTemplateDescription || DEFAULT_DESCRIPTION;
                }
                const loaded = {
                    title: d.publish_title || d.video_file_name || '',
                    description: desc,
                    tags: d.publish_hashtags || [],
                };
                if (cancelled) return;
                setTitle(loaded.title);
                setDescription(loaded.description);
                setTags(loaded.tags);
                setSaved(loaded);
                // Seed missing publish_* fields so posting to YouTube works without a manual save
                if (!d.publish_title || !d.publish_description || !d.publish_hashtags) {
                    await setDoc(bumpRef, { publish_title: loaded.title, publish_description: loaded.description, publish_hashtags: loaded.tags }, { merge: true });
                }
            } catch (error) {
                console.error('Error loading bump details:', error);
            }
        })();
        return () => { cancelled = true; };
    }, [user.uid, videoId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Live thumbnail (older bumps store a storage path; swap it for its download URL once)
    useEffect(() => {
        return onSnapshot(bumpRef, async snap => {
            const thumb = snap.data()?.video_thumbnail;
            if (thumb?.startsWith('bumpThumbnails/')) {
                try {
                    const url = await getDownloadURL(ref(storage, `bumpThumbnails/${user.uid}/${videoId}`));
                    setThumbnailUrl(url);
                    await setDoc(bumpRef, { video_thumbnail: url }, { merge: true });
                } catch (error) {
                    console.error('Error fetching thumbnail URL:', error);
                }
            } else {
                setThumbnailUrl(thumb || placeholder_color_thumbnail);
            }
        }, () => {});
    }, [user.uid, videoId]); // eslint-disable-line react-hooks/exhaustive-deps

    const isDirty = !!saved && (title !== saved.title || description !== saved.description || JSON.stringify(tags) !== JSON.stringify(saved.tags));

    const saveEdits = async () => {
        setIsSaving(true);
        try {
            await setDoc(bumpRef, { publish_title: title, publish_description: description, publish_hashtags: tags }, { merge: true });
            setSaved({ title, description, tags });
            localStorage.removeItem(`videoCache_${user.uid}_BumpedVideos`); // the /bumper list shows publish_title
            setJustSaved(true);
            setTimeout(() => setJustSaved(false), 2000);
        } catch (error) {
            console.error('Error saving edits:', error);
        }
        setIsSaving(false);
    };

    // Same substitution postYoutube does server-side, for the Preview popup. The description, takeaways and
    // hashtags variables only have content on videos processed before April 2026; otherwise they come out blank.
    const processDescription = text => text
        .split('{{VIDEO_DESCRIPTION}}').join(summary)
        .split('{{VIDEO_TAKEAWAYS}}').join(takeaways.join('\n'))
        .split('{{VIDEO_TIMESTAMPS}}').join(typeof timestamps === 'string' ? timestamps : '')
        .split('{{VIDEO_HASHTAGS}}').join(hashtags.map(tag => `#${tag}`).join(' '));

    const handleThumbnailUpload = (event) => {
        const file = event.target.files[0];
        event.target.value = '';
        if (!file) return;
        if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > 1024 * 1024) {
            alert('Please upload a valid PNG or JPEG file under 1 MB.');
            return;
        }
        const objectUrl = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(objectUrl);
            const { width, height } = img;
            const isValid = (width === 1280 && height === 720) || (width >= 640 && height >= 360 && width * 9 === height * 16);
            if (!isValid) {
                alert('Please upload a thumbnail with dimensions of 1280x720 pixels or a minimum width of 640 pixels and a 16:9 aspect ratio.');
                return;
            }
            setIsThumbnailUploading(true);
            setLocalThumbnailUrl(placeholder_dark_thumbnail);
            const uploadTask = uploadBytesResumable(ref(storage, `bumpThumbnails/${user.uid}/${videoId}`), file);
            uploadTask.on('state_changed', null,
                error => {
                    console.error('Error uploading thumbnail:', error);
                    setLocalThumbnailUrl(null);
                    setIsThumbnailUploading(false);
                },
                async () => {
                    try {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        setLocalThumbnailUrl(downloadURL);
                        await setDoc(bumpRef, { video_thumbnail: downloadURL }, { merge: true });
                        localStorage.removeItem(`videoCache_${user.uid}_BumpedVideos`);
                    } catch (error) {
                        console.error('Error saving thumbnail:', error);
                    }
                    setIsThumbnailUploading(false);
                }
            );
        };
        img.src = objectUrl;
    };

    const addTags = newTags => {
        const room = MAX_TAG_CHARS - tags.join('').length;
        const accepted = [];
        let used = 0;
        for (const tag of newTags) {
            if (used + tag.length > room) break;
            accepted.push(tag);
            used += tag.length;
        }
        if (accepted.length) setTags([...tags, ...accepted]);
    };

    const handleTagInput = (e) => {
        const input = e.target.value;
        if (input.includes(',')) {
            addTags(input.split(',').map(tag => tag.trim()).filter(Boolean));
            setTagInput('');
        } else if (tags.join('').length + input.length <= MAX_TAG_CHARS) {
            setTagInput(input);
        }
    };

    const handleTagKeyDown = (e) => {
        if (e.key === 'Enter' && tagInput.trim()) {
            e.preventDefault();
            addTags([tagInput.trim()]);
            setTagInput('');
        }
    };

    const removeTag = index => setTags(tags.filter((_, i) => i !== index));
    const clearAllTags = () => setTags([]);

    const handleTagDoubleClick = (index) => {
        setEditingIndex(index);
        setEditingTag(tags[index]);
    };

    const commitTagEdit = () => {
        const value = editingTag.trim();
        setTags(value ? tags.map((t, i) => (i === editingIndex ? value : t)) : tags.filter((_, i) => i !== editingIndex));
        setEditingIndex(-1);
    };

    return (
        <div className="editBump-section-content">
            <div className="editBump-section-details">
                <div className="editBump-section-input-group">
                    <div className="editBump-title-header">
                        <div className="editBump-section-title-label">Title (required)</div>
                        <button
                            className="editBump-title-button"
                            onClick={saveEdits}
                            disabled={!isDirty || isSaving || !title.trim()}
                        >
                            {justSaved ? 'Saved' : 'Save'}
                        </button>
                    </div>
                    <div className="editBump-title-wrapper">
                        <textarea
                            className="editBump-section-title-input"
                            placeholder="Add a title that describes your video"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            maxLength={100}
                        />
                    </div>
                    <div className="editBump-input-character-count">{title.length}/100</div>
                </div>

                <div className="editBump-section-input-group">
                    <div className="editBump-description-header">
                    <div className="editBump-description-label-wrapper">
                            <div className="editBump-section-description-label">Description</div>
                            <div className="editBump-section-description-help-text">
                            <div>Use variables like <code>{'{{VIDEO_TIMESTAMPS}}'}</code> for Bump AI content. <a href="/help/articles/9742235-what-are-the-bump-ai-variables">Learn more</a></div>
                            </div>
                        </div>
                        <div className="button-link-wrapper">
                            <button
                                className="previewBump-button"
                                onClick={() => setIsModalOpen(true)}
                                disabled={!isProcessedbyAI}
                            >
                                { 
                                isTranscriptEmpty 
                                    ? 'No Audio'
                                    : (isProcessedbyAI 
                                        ? (device === 'tablet' || device === 'mobile' ? 'View' : 'Preview') 
                                        : (device === 'tablet' || device === 'mobile' ? 'Creating...' : 'Creating...'))
                                }
                            </button>
                            <Link to="/settings/creator" className="editBump-settingTemplateIcon-button" title="Description template" aria-label="Description template settings">
                                <FontAwesomeIcon icon={faGear} />
                            </Link>
                            </div>
                    </div>
                    <div className="editBump-description-wrapper">
                        <textarea
                            className="editBump-section-description"
                            placeholder="Tell viewers about your video"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            maxLength={5000}
                        />
                    </div>
                    <div className="editBump-textarea-character-count">{description.length}/5000</div>
                </div>

                <div className="editBump-section-input-group">
                    <div className="editBump-section-thumbnail-label">Thumbnail</div>
                    <div className="editBump-thumbnail-description">Set a thumbnail that stands out and draws viewers' attention. <a href="/help/articles/9742267-how-do-i-set-the-perfect-thumbnail-for-my-video">Learn more</a></div>
                    <div className="editBump-thumbnail-container">
                            <div
                                className="editBump-thumbnail-upload-area"
                                onClick={() => !isThumbnailUploading && fileInputRef.current.click()}
                                style={{ opacity: isThumbnailUploading ? 0.5 : 1, pointerEvents: isThumbnailUploading ? 'none' : 'auto' }}
                            >
                                <FontAwesomeIcon icon={faImage} className="editBump-upload-icon" />
                                <span>Upload file</span>
                            </div>
                            <input
                                type="file"
                                id="thumbnailInput"
                                ref={fileInputRef}
                                accept="image/png, image/jpeg"
                                onChange={handleThumbnailUpload}
                                hidden
                            />
                            <div className="editBump-thumbnail-current">
                                <img src={localThumbnailUrl || thumbnailUrl || placeholder_color_thumbnail} alt="Current Thumbnail" />
                            </div>
                        </div>
                </div>


                <div className="editBump-section-input-group">
                    <div className="editBump-section-tags-label">Tags</div>
                    <div className="editBump-tags-description">
                    Tags are hashtags that make your videos more discoverable by associating them with specific topics, trends, or communities. 
                    <a href="/help/articles/9381190-how-do-video-tags-help-you"> Learn more</a>
                    </div>
                    <div className="editBump-tags-input-wrapper">
                        {tags.map((tag, index) => (
                            <div className="editBump-tag" key={index}>
                                {editingIndex === index ? (
                                    <input
                                        type="text"
                                        className="editBump-tag-input"
                                        value={editingTag}
                                        onChange={e => setEditingTag(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                                        onBlur={commitTagEdit}
                                        autoFocus
                                    />
                                ) : (
                                    <span onDoubleClick={() => handleTagDoubleClick(index)}>
                                        {tag}
                                    </span>
                                )}
                                <span className="editBump-tag-close" onClick={() => removeTag(index)}>×</span>
                            </div>
                        ))}
                        <input
                            type="text"
                            className="editBump-section-tags-input"
                            placeholder="Add tag"
                            value={tagInput}
                            onChange={handleTagInput}
                            onKeyDown={handleTagKeyDown}
                        />
                        {tags.length > 0 && (
                            <button className="editBump-clear-tags-button" onClick={clearAllTags}>×</button>
                        )}
                    </div>
                    <div className="editBump-textarea-character-count">{tags.join('').length}/500</div>
                </div>
            </div>
            <PreviewBumpPopup 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                content={processDescription(description)} 
            />
        </div>
    );


};

export default UserEditBumpSection;
